/**
 * ToneService - Main service for managing Tone.js integration
 * Combines VoiceFactory and SequencerService for complete audio management
 */

import * as Tone from 'tone';
import { createVoices, type Voice } from './VoiceFactory';
import { SequencerService, type StepChangeCallback } from './SequencerService';
import type { InstrumentType, ADSRSettings } from '../../types/composition';
import type { InstrumentParameters } from '../../types/instruments';

/**
 * ToneService manages the complete Tone.js lifecycle including voices and sequencer
 */
export class ToneService {
  private voices: Voice[] = [];
  private sequencer: SequencerService;
  private isInitialized: boolean = false;
  private volumeLevels: number[] = []; // Track volume levels for each voice

  constructor() {
    this.sequencer = new SequencerService();
  }

  /**
   * Initialize Tone.js audio context
   * Must be called after user interaction
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    await Tone.start();
    this.isInitialized = true;
  }

  /**
   * Create or recreate voices with new instrument types
   * 
   * @param instrumentTypes - Array of instrument types
   * @param adsrSettings - Optional ADSR settings per instrument (legacy support)
   * @param instrumentParameters - Optional full instrument parameters per instrument
   * @param parametersEnabled - Optional array of flags indicating whether to apply parameters per instrument
   */
  createVoices(
    instrumentTypes: InstrumentType[],
    adsrSettings?: ADSRSettings[],
    instrumentParameters?: (InstrumentParameters | undefined)[],
    parametersEnabled?: boolean[]
  ): void {
    // Dispose old voices if they exist
    this.disposeVoices();
    
    // Create new voices
    this.voices = createVoices(instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled);
    
    // Initialize volume levels to default (0.8)
    this.volumeLevels = Array(instrumentTypes.length).fill(0.8);
  }
  
  /**
   * Update parameters for a specific voice in real-time
   * 
   * @param row - Row index
   * @param parameters - New instrument parameters
   */
  updateVoiceParameters(row: number, parameters: InstrumentParameters): void {
    if (this.voices[row]) {
      this.voices[row].updateParameters(parameters);
    }
  }

  /**
   * Initialize the sequencer with current voices and grid state
   * 
   * @param gridState - 2D array of active cells
   * @param noteState - 2D array of note pitches
   * @param steps - Number of steps in pattern
   * @param bpm - Beats per minute
   * @param onStepChange - Callback for step changes
   */
  initSequencer(
    gridState: boolean[][],
    noteState: (string | null)[][],
    steps: number,
    bpm: number,
    onStepChange?: StepChangeCallback
  ): void {
    this.sequencer.initSequencer(
      this.voices,
      gridState,
      noteState,
      steps,
      bpm,
      onStepChange
    );
  }

  /**
   * Start playback
   */
  async play(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    await this.sequencer.start();
  }

  /**
   * Pause playback
   */
  pause(): void {
    this.sequencer.pause();
  }

  /**
   * Stop playback
   */
  stop(): void {
    this.sequencer.stop();
  }

  /**
   * Set BPM
   * 
   * @param bpm - New beats per minute
   */
  setBpm(bpm: number): void {
    this.sequencer.setBpm(bpm);
  }

  /**
   * Set loop mode
   * 
   * @param enabled - Whether looping is enabled
   */
  setLoop(enabled: boolean): void {
    this.sequencer.setLoop(enabled);
  }

  /**
   * Set volume for a specific voice/row
   * 
   * @param row - Row index
   * @param volume - Volume level (0.0 - 1.0)
   */
  setVolume(row: number, volume: number): void {
    if (this.voices[row]) {
      this.volumeLevels[row] = volume;
      this.voices[row].gainNode.gain.value = volume;
    }
  }

  /**
   * Mute or unmute a specific voice/row
   * 
   * @param row - Row index
   * @param muted - Whether to mute
   */
  setMute(row: number, muted: boolean): void {
    if (this.voices[row]) {
      // Mute by setting gain to 0, unmute by restoring to saved volume level
      this.voices[row].gainNode.gain.value = muted ? 0 : (this.volumeLevels[row] ?? 0.8);
    }
  }

  /**
   * Get current transport state
   */
  getState(): 'started' | 'paused' | 'stopped' {
    return this.sequencer.getState();
  }

  /**
   * Check if Tone.js is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Dispose of voices
   */
  private disposeVoices(): void {
    this.voices.forEach(voice => {
      voice.dispose();
    });
    this.voices = [];
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    this.sequencer.dispose();
    this.disposeVoices();
    this.isInitialized = false;
  }
}

// Export singleton instance
export const toneService = new ToneService();
